/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 *
 * Full Workflows Table Component with EOS/ETMF support and multi-selection.
 * Based on lego/page/workflows/Table.jsx
 */

import { FC, useEffect, useState, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { useServiceContext } from '../../root'
import { Workflow } from './types'
import { WorkflowStatus } from '../../constants/workflow'
import { Role } from '../../constants/user'
import { RcmTable, RcmTableProps } from '../../components/rcm-table/RcmTable'
import { TableHeadRow, TableHeadCell, TableRow } from '../../components/table/TableComponents'
import { useRcmTableData } from '../../hooks/useRcmTableData'
import { useModal } from '../../contexts/ModalContext'
import WorkflowDetailsModal from '../../components/modals/WorkflowDetailsModal'
import TextLinkButton from '../../components/shared/TextLinkButton'
import TableDateTimeCell from '../../components/shared/TableDateTimeCell'
import TaskDocLink from '../../components/shared/TaskDocLink'
import WorkflowsTableMenu from './WorkflowsTableMenu'
import {
  TableMultiSelectionWrapper,
  SelectAllRows,
  SelectRow,
  TableMultiSelectionConsumer
} from '../../components/rcm-table/TableMultiSelection'

interface WorkflowsTableFullProps extends Omit<RcmTableProps, 'HeadRow' | 'Row'> {
  filterShown?: boolean
  viewType?: string
  isEosEnabled?: boolean
  isEtmfEnabled?: boolean
  TableFooter?: ReactNode
}

/**
 * Full Workflows Table Component
 *
 * This is the comprehensive workflows table with support for:
 * - EOS view (shows Study/Site columns)
 * - ETMF view (shows Study/Country/Site columns instead of Initiator/Current Phase)
 * - Multi-selection with batch cancel operations (active view only)
 * - Progress bar visualization for workflow status
 * - Workflow details modal integration
 * - Actions menu with full management options
 *
 * Migrated from lego/page/workflows/Table.jsx
 */
const WorkflowsTableFull: FC<WorkflowsTableFullProps> = ({
  filterShown = false,
  viewType = 'active',
  isEosEnabled = false,
  isEtmfEnabled = false,
  ...restProps
}) => {
  const { t } = useTranslation()
  const { service, api } = useServiceContext()
  const tableData = useRcmTableData()
  const { showModal } = useModal()

  const [isEosView, setIsEosView] = useState(false)
  const [isEtmfView, setIsEtmfView] = useState(false)

  const auth = service?.auth

  useEffect(() => {
    setIsEosView(
      isEosEnabled
        ? true
        : !!(tableData.filter?.isEosEnabled && (auth?.getRole?.() === Role.USER || tableData.filter?.isEosSelected))
    )
    setIsEtmfView(isEtmfEnabled ? true : !!tableData.filter?.isEtmfEnabled)
  }, [tableData.entries, isEosEnabled, isEtmfEnabled])

  /**
   * Get current phase display text based on workflow status
   */
  const getCurrentPhase = (flow: Workflow): string => {
    if (flow.status === WorkflowStatus.CANCELLED) {
      return t('WORKFLOWS_STEP_STATUS_CANCELLED')
    } else if (flow.status === WorkflowStatus.COMPLETED || flow.status === WorkflowStatus.FINISHED) {
      return t('WORKFLOWS_STEP_STATUS_FINISHED')
    }
    return flow.currentPhase || ''
  }

  /**
   * Handle workflow row click to open details modal
   */
  const handleRowClick = (workflow: Workflow) => {
    showModal(<WorkflowDetailsModal wfId={workflow.id} />)
  }

  return (
    <div>
      <TableMultiSelectionWrapper
        texts={{
          selectAllItemsText: '',
          allItemsSelectedText: 'DOCUMENTS_MULTISELECT_ALL_N_ITEMS_SELECTED'
        }}
        isWorkflowCancelled={true}
        isWorkflow={true}
        getTotalNumberFunction={() => {
          return api?.workflows?.workflows
            ?.getList({
              pageSize: 20,
              pageStart: 0,
              sortBy: 'START_DATE',
              sortDesc: true
            })
            .then((data: any) => data)
        }}
      >
        <RcmTable
          className="workflow-list-table chita-table"
          onRowEnter={(row: any) => {
            showModal(<WorkflowDetailsModal wfId={row.id} />)
          }}
          disableOnInitLoad={filterShown}
          HeadRow={
            <TableHeadRow className="has-actions">
              {viewType === 'active' && (
                <th className="clipped-cell with-checkbox" id="workflow-select-all">
                  <SelectAllRows />
                </th>
              )}
              <TableHeadCell sort="ID">{t('NUMBER')}</TableHeadCell>
              {isEosView && <TableHeadCell id="eos-study">{t('EMTF_MD_STUDY')}</TableHeadCell>}
              {isEosView && <TableHeadCell id="eos-site">{t('EMTF_MD_SITE')}</TableHeadCell>}
              <TableHeadCell sort="TYPE">{t('TYPE')}</TableHeadCell>
              <TableHeadCell id="status">{t('STATUS')}</TableHeadCell>
              {!isEosView && <TableHeadCell id="document-name">{t('DOCUMENT_NAME')}</TableHeadCell>}
              <TableHeadCell sort="START_DATE" style={{ width: '190px' }}>
                {t('START_DATE')}
              </TableHeadCell>
              {!isEtmfView && <TableHeadCell sort="INITIATOR">{t('INITIATOR')}</TableHeadCell>}
              {!isEtmfView && <TableHeadCell id="current-phase">{t('CURRENT_PHASE')}</TableHeadCell>}
              {isEtmfView && <TableHeadCell id="etmf-study">{t('EMTF_MD_STUDY')}</TableHeadCell>}
              {isEtmfView && <TableHeadCell id="etmf-country">{t('EMTF_MD_COUNTRY')}</TableHeadCell>}
              {isEtmfView && <TableHeadCell id="etmf-site">{t('REPORTS_FILTER_LABEL_SITE')}</TableHeadCell>}
              <TableHeadCell id="actions" className="actions">
                {t('ACTIONS')}
              </TableHeadCell>
            </TableHeadRow>
          }
          Row={({ row }: { row: any }) => {
            const flow = row as Workflow

            return (
              <TableRow key={flow.id}>
                {viewType === 'active' && (
                  <td className="clipped-cell with-checkbox">
                    <SelectRow row={{ id: String(flow.id) }} disabled={false} />
                  </td>
                )}
                <td>
                  <TextLinkButton action={() => handleRowClick(flow)}>{flow.formattedId}</TextLinkButton>
                </td>
                {isEosView && (
                  <>
                    <td>{flow.study?.name ?? '–'}</td>
                    <td>{flow.site?.name ?? '–'}</td>
                  </>
                )}
                <td>
                  <TextLinkButton action={() => handleRowClick(flow)} className="word-break" title={flow.localizedType}>
                    {flow.localizedType}
                  </TextLinkButton>
                </td>
                <td className="status">
                  <div
                    onClick={() => handleRowClick(flow)}
                    className={
                      'status-wrapper' +
                      (flow.status === WorkflowStatus.CANCELLED ||
                      flow.status === WorkflowStatus.SUSPENDED ||
                      flow.rejected
                        ? ' canceled'
                        : '')
                    }
                  >
                    <span style={{ width: `${flow.progress}%` }} className={flow.progress === 100 ? 'w100p' : ''} />
                  </div>
                </td>
                {!isEosView && (
                  <td className="doc-name clipped-cell">
                    <TaskDocLink task={flow as any} isWorkflow />
                  </td>
                )}
                <td>
                  <TableDateTimeCell val={flow.startDate} />
                </td>
                {!isEtmfView && <td>{flow.initiator?.name ?? ''}</td>}
                {!isEtmfView && <td>{getCurrentPhase(flow)}</td>}
                {isEtmfView && (
                  <>
                    <td>{flow.filteredFileToEtmfDocDetails?.study ?? '-'}</td>
                    <td>{flow.filteredFileToEtmfDocDetails?.country ?? '-'}</td>
                    <td>{flow.filteredFileToEtmfDocDetails?.site ?? '-'}</td>
                  </>
                )}
                <td className="actions">
                  <TableMultiSelectionConsumer>
                    {(tableMultiSelection) => {
                      return (
                        <WorkflowsTableMenu
                          workflow={flow}
                          multiSelectOptions={{
                            selectedIds:
                              tableMultiSelection?.multiSelectState?.selectedIds?.map((id) => Number(id)) || [],
                            selectedWholeResult: tableMultiSelection?.multiSelectState?.selectedWholeResult || false,
                            selectedItems: tableMultiSelection?.multiSelectState?.selectedIds
                              ? (tableData.entries.filter((wf: any) =>
                                  tableMultiSelection.multiSelectState.selectedIds.includes(String(wf.id))
                                ) as Workflow[])
                              : [],
                            deselectAll: tableMultiSelection?.deselectAll
                          }}
                        />
                      )
                    }}
                  </TableMultiSelectionConsumer>
                </td>
              </TableRow>
            )
          }}
          {...restProps}
        />
      </TableMultiSelectionWrapper>
    </div>
  )
}

export default WorkflowsTableFull
